import { AfterViewInit, ChangeDetectorRef, Component, Input, OnChanges } from "@angular/core";
import { TranslateModule, TranslateService } from "@ngx-translate/core";
import { MeasuringPointApi } from "@ramudden/data-access/resource/measuring-point.api";
import { IAuditTrail } from "@ramudden/models/audit-trail";
import { IDevice } from "@ramudden/models/device";
import { IJournal } from "@ramudden/models/journal";
import { IMeasuringPoint, IMeasuringPointNavigator } from "@ramudden/models/measuring-point";
import { Tooltip } from "primeng/tooltip";
import { MomentTimePipe } from "../../pipes/datetime.pipe";
import { TranslateModelPipe } from "../../pipes/translate-model.pipe";
import { DomainDataFieldComponent } from "../domain-data-field/domain-data-field.component";
import { NavigatorComponent } from "../navigator/navigator.component";
import { SvgIconComponent } from "../svg-icon/svg-icon.component";

class DayChangeOverview {
    id: number;
    journal: IJournal;
    auditTrail: IAuditTrail;
    measuringPoint: IMeasuringPointNavigator;
    organizationId: number;
    device: IDevice;

    constructor(readonly timestamp: Date) {}

    static fromJournal(journal: IJournal): DayChangeOverview {
        const dayChangeOverview = new DayChangeOverview(journal.timestamp);
        dayChangeOverview.id = journal.id;
        dayChangeOverview.journal = journal;
        dayChangeOverview.device = journal.device;
        dayChangeOverview.measuringPoint = journal.measuringPoint;
        dayChangeOverview.organizationId = journal.organizationId;
        return dayChangeOverview;
    }

    static fromAuditTrail(auditTrail: IAuditTrail): DayChangeOverview {
        const dayChangeOverview = new DayChangeOverview(auditTrail.timeStamp);
        // Avoid conflicts between `journal.id` and `auditTrail.id`
        dayChangeOverview.id = 0 - auditTrail.id;
        dayChangeOverview.auditTrail = auditTrail;
        dayChangeOverview.device = auditTrail.device;
        dayChangeOverview.measuringPoint = auditTrail.measuringPoint;
        dayChangeOverview.organizationId = auditTrail.ownerId;
        return dayChangeOverview;
    }
}

@Component({
    selector: "app-measuring-point-day-change-overview",
    templateUrl: "./measuring-point-day-change-overview.component.html",
    imports: [
        DomainDataFieldComponent,
        TranslateModelPipe,
        Tooltip,
        NavigatorComponent,
        MomentTimePipe,
        SvgIconComponent,
        TranslateModule,
    ],
})
export class MeasuringPointDayChangeOverviewComponent implements OnChanges, AfterViewInit {
    @Input() measuringPoint: IMeasuringPoint;
    @Input() date: Date;

    overviews: DayChangeOverview[];
    private journals: IJournal[];
    private auditTrails: IAuditTrail[];
    private initialized = false;

    constructor(
        public translateService: TranslateService,
        private readonly cd: ChangeDetectorRef,
        private readonly measuringPointApi: MeasuringPointApi,
    ) {}

    ngOnChanges() {
        if (!this.initialized) return;

        this.fetchData();
    }

    ngAfterViewInit() {
        this.initialized = true;

        this.fetchData();
    }

    overviewTrackByFn(index: number, item: DayChangeOverview) {
        return item.id;
    }

    private async fetchData() {
        if (!this.measuringPoint || !this.date) return;

        this.journals = null;
        this.auditTrails = null;

        this.measuringPointApi
            .getEventOverview$(this.measuringPoint.id, this.date, new Date(this.date).addDays(1))
            .subscribe((result) => {
                this.auditTrails = result.audits;
                this.journals = result.journals;
                this.bundleData();
            });
    }

    private async bundleData() {
        const journalDayOverviews = (this.journals || []).map((x) => DayChangeOverview.fromJournal(x));
        const auditTrailDayOverviews = (this.auditTrails || []).map((x) => DayChangeOverview.fromAuditTrail(x));

        this.overviews = journalDayOverviews.concat(auditTrailDayOverviews).sortBy((x) => x.timestamp.getTime());
        this.cd.detectChanges();
    }
}
